import { isEmpty } from 'lodash-es';
import { defineStore } from 'pinia';
import { AxiosResponse } from 'axios';

import apiGw from '@/services/apiGw';
import {
  PartnerEngagement,
  Partnership,
  PartnershipEngagement,
  PartnershipObjective,
  PartnershipStatus,
  PartnershipURI,
  Role
} from '@/types/partnership';
import { createResourceUri, getUidFromSelfLink, getUidsFromUri } from '@/utils/links';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import { useUserStore } from '@/store/userStore';
import { useCashAccountsStore } from '@/store/cashAccountsStore';
import { VirtualIdentity, VirtualIdentityType } from '@/types/virtualIdentities';
import { AgentURI } from '@/types/agent';
import { Subscription } from '@/components/billing/types/subscription';
import { Delegate, RoleAccessMode } from '@/components/cards/types/delegate';

export type PartnershipType = PartnershipEngagement | Partnership;

export function isPartnership(part: PartnershipType): part is Partnership {
  return (part as Partnership).contactDetails !== undefined;
}

export function isPartnershipEngagement(part: PartnershipType): part is PartnershipEngagement {
  return (part as PartnershipEngagement).requestStatus !== undefined;
}

type Store = {
  partnerships: Map<string, PartnershipType>;
  pendingPromise: { [key: string]: Promise<AxiosResponse> };
  pendingUpdatePartnership: boolean;
};

export const usePartnershipsStore = defineStore('partnerships', {
  state: (): Store => ({
    partnerships: new Map<string, PartnershipType>(),
    pendingPromise: {},
    pendingUpdatePartnership: false
  }),
  getters: {
    currentPartnership: (state): Partnership | undefined => {
      const contextSwitchStore = useContextSwitchStore();
      const ctxUid = contextSwitchStore.currentContextUid;
      if (!ctxUid) return undefined;

      return state.partnerships.get(ctxUid) as Partnership;
    },
    isLoading: (state) => {
      return Object.keys(state.pendingPromise).length > 0;
    },
    getPartnerships: (state) => {
      return Array.from(state.partnerships.values());
    },
    getBankablePartnerships(state) {
      return Array.from(state.partnerships.values()).filter(
        (p) => isPartnership(p) || (isPartnershipEngagement(p) && p.status === PartnershipStatus.Bankable)
      );
    },
    getPendingUpdatePartnership: (state) => {
      return state.pendingUpdatePartnership;
    },
    hasPartnerships: (state) => {
      return state.partnerships.size > 0;
    },
    hasEbillActivatedForCurrentPartnership: (state) => {
      const contextSwitchStore = useContextSwitchStore();
      const ctxUid = contextSwitchStore.currentContextUid;
      if (!ctxUid) return false;
      const currentPartnership = state.partnerships.get(ctxUid) as Partnership;
      if (!currentPartnership?.virtualIdentities) return false;
      return currentPartnership.virtualIdentities.some(
        (vi: VirtualIdentity) => vi.typeName === VirtualIdentityType.EbillBusiness
      );
    },
    isPartnerOrDelegateForCurrentPartnership(state) {
      const contextSwitchStore = useContextSwitchStore();
      const userStore = useUserStore();
      const ctxUid = contextSwitchStore.currentContextUid;

      if (!ctxUid) return false;
      const currentPartnership = state.partnerships.get(ctxUid) as Partnership;
      if (!currentPartnership) return false;

      const isPartner = currentPartnership.partners?.some((vi: Delegate) => vi.personUid === userStore.uid);
      const isDelegate = currentPartnership.delegates?.some((vi: Delegate) => vi.personUid === userStore.uid);

      return isPartner || isDelegate;
    },
    isRequestorBootstrapperForCurrentPartnership(state) {
      const contextSwitchStore = useContextSwitchStore();
      const ctxUid = contextSwitchStore.currentContextUid;

      if (!ctxUid) return false;
      const currentPartnership = state.partnerships.get(ctxUid) as Partnership;
      if (!currentPartnership) return false;

      const isBootstrapper = currentPartnership.partnerEngagements?.some(
        (item: PartnerEngagement) => item.roles.includes(Role.Bootstrapper) && item.roles.length === 1
      );

      const hasRequestorAndBootstrapperOnly = currentPartnership.partnerEngagements?.some(
        (item: PartnerEngagement) =>
          item.roles.includes(Role.Requestor) && item.roles.includes(Role.Bootstrapper) && item.roles.length === 2
      );

      return isBootstrapper || hasRequestorAndBootstrapperOnly;
    },
    hasRoleForCurrentPartnership: (state) => {
      return (role: Role) => {
        const contextSwitchStore = useContextSwitchStore();
        const userStore = useUserStore();
        const ctxUid = contextSwitchStore.currentContextUid;

        if (!ctxUid) return false;
        const currentPartnership = state.partnerships.get(ctxUid) as Partnership;
        if (!currentPartnership) return false;

        if (role === Role.Partner) {
          return (
            Array.isArray(currentPartnership.partners) &&
            currentPartnership.partners.some((vi: Delegate) => vi.personUid === userStore.uid)
          );
        } else if (role === Role.Delegate) {
          return (
            Array.isArray(currentPartnership.delegates) &&
            currentPartnership.delegates.some((vi: Delegate) => vi.personUid === userStore.uid)
          );
        } else if (role === Role.Deputy) {
          return (
            Array.isArray(currentPartnership.deputies) &&
            currentPartnership.deputies.some((vi: Delegate) => vi.personUid === userStore.uid)
          );
        }

        return false;
      };
    }
  },
  actions: {
    setPartnerships(partnerships: Array<PartnershipType>) {
      this.partnerships = new Map(partnerships.map((t) => [t.uid, t]));
    },
    appendPartnerships(partnerships: Array<PartnershipType>) {
      const extraPartnerships = new Map(partnerships.map((t) => [t.uid, t]));
      this.partnerships = new Map([...this.partnerships, ...extraPartnerships]);
    },
    updatePartnership(partnership: PartnershipType) {
      this.partnerships.set(partnership.uid, partnership);
    },
    async loadPartnerships(uri: string, forceReload = false) {
      if (!forceReload && this.partnerships.size > 0) {
        // partnerships already loaded
        return;
      }

      const promise = this.pendingPromise[uri];
      if (!!promise) {
        // partnerships already requested for this uri
        await promise;
      } else {
        const request = apiGw.get(uri);
        this.pendingPromise[uri] = request;
        const response = await request;

        this.appendPartnerships(response.data.requestedPartnerships);
        delete this.pendingPromise[uri];
      }
    },
    async loadCurrentPartnershipAndCashAccounts() {
      const contextSwitchStore = useContextSwitchStore();
      const ctxUid = contextSwitchStore.currentContextUid;
      const partnership = this.getPartnerships.filter((p) => ctxUid === p.uid)[0];

      if (partnership && ctxUid && !this.isRequestorBootstrapperForCurrentPartnership) {
        const currentPartnership = await this.getPartnershipDetails(partnership.uid);
        if (currentPartnership) {
          const cashAccountsUri = createResourceUri(PartnershipURI.Base, {
            uid: currentPartnership.uid,
            resource: PartnershipURI.CashAccounts
          });

          const cashAccountsStore = useCashAccountsStore();
          cashAccountsStore.loadCashAccounts(cashAccountsUri).then(() => {
            const cashAccounts4ctx = cashAccountsStore.entitledCashAccountsForCurrentContext;
            // no main cash account for now, take billing account as main account
            const mainCashAccountUri = cashAccounts4ctx.filter((ca) => ca.billingAccount)[0]?._links.self.uri;

            const isEntitledToMainCashAccount =
              mainCashAccountUri && cashAccounts4ctx.map((c) => c._links.self.uri).includes(mainCashAccountUri);

            let defaultCashAccountUid = undefined;
            if (mainCashAccountUri && isEntitledToMainCashAccount) {
              defaultCashAccountUid = getUidsFromUri(mainCashAccountUri)[0];
            } else if (cashAccounts4ctx.length) {
              defaultCashAccountUid = getUidFromSelfLink(cashAccounts4ctx[0]._links.self);
            }

            cashAccountsStore.selectCashAccount(defaultCashAccountUid);
          });
        }
      }
    },
    async getPartnershipDetails(partnershipUid: string): Promise<Partnership | undefined> {
      const part = this.partnerships.get(partnershipUid);
      const partUri = createResourceUri(PartnershipURI.BaseV2, {
        uid: partnershipUid,
        queryParams: { extIds: true, contactDetails: true }
      });

      if (part && isPartnership(part)) {
        return part;
      }

      const userStore = useUserStore();
      if (userStore.isRegistered) return undefined;
      /**
       * Avoid duplicate calls to the gateway in case we already requested the same resource
       */
      const promise = this.pendingPromise[partUri];
      if (!!promise) {
        await promise;
      } else {
        const request = apiGw.get(partUri);
        this.pendingPromise[partUri] = request;
        const response = await request;

        this.partnerships.set(partnershipUid, { ...response.data, partnerEngagements: part?.partnerEngagements });
        await this.loadAdditionalPartnershipData();
        // now that we have the partnership details we can enrich the contextSwitch
        const contextSwitchStore = useContextSwitchStore();
        const ctx = contextSwitchStore.contexts[partnershipUid];
        if (ctx) {
          const updated = { ...ctx };
          const partnershipDetails: Partnership = response.data;
          if (partnershipDetails.externalIds) {
            updated.registeryUid = partnershipDetails.externalIds.filter((i) => i.scheme === 'uid_reg')[0]?.value;
          }

          updated.addressLine1 = !isEmpty(partnershipDetails.contactDetails?.domicileAddress)
            ? `${partnershipDetails.contactDetails.domicileAddress?.street} ${partnershipDetails.contactDetails.domicileAddress?.number}`
            : undefined;
          updated.addressLine2 = !isEmpty(partnershipDetails.contactDetails?.domicileAddress)
            ? `${partnershipDetails.contactDetails.domicileAddress?.postalCode} ${partnershipDetails.contactDetails.domicileAddress?.city} (${partnershipDetails.contactDetails.domicileAddress?.country})`
            : undefined;

          contextSwitchStore.updateContext(ctx.uid, updated);
        }

        delete this.pendingPromise[partUri];
      }
      return this.partnerships.get(partnershipUid) as Partnership;
    },
    async loadAdditionalPartnershipData() {
      const partnership = this.currentPartnership;
      if (!partnership) return;

      const [objectives, virtualIdentities, partners, delegates, deputies] = await Promise.all([
        this.loadPartnershipObjectivesForCurrentPartnership(),
        this.loadPartnershipVirtualIdentitiesForCurrentPartnership(),
        this.loadPartnersForCurrentPartnership(RoleAccessMode.Granted),
        this.loadDelegatesForCurrentPartnership(RoleAccessMode.Granted),
        this.loadDeputiesForCurrentPartnership(RoleAccessMode.Granted)
      ]);

      const updatedPartnership = {
        ...partnership,
        objectives,
        virtualIdentities,
        partners,
        delegates,
        deputies
      };

      this.updatePartnership(updatedPartnership);
    },
    async loadPartnershipObjectivesForCurrentPartnership() {
      const partnership = this.currentPartnership;
      if (!partnership) return;
      const res = await apiGw.get(
        createResourceUri(PartnershipURI.BaseV2, { uid: partnership.uid, resource: PartnershipURI.Objectives })
      );
      const objectives = (res.data as PartnershipObjective).data.serviceObjectives;
      this.updatePartnership({ ...partnership, objectives });
      return objectives;
    },
    async loadPartnershipVirtualIdentitiesForCurrentPartnership() {
      const partnership = this.currentPartnership;
      if (!partnership) return;
      const res = await apiGw.get(
        createResourceUri(PartnershipURI.BaseV2, { uid: partnership.uid, resource: PartnershipURI.VirtualIdentities })
      );
      const virtualIdentities = res.data as VirtualIdentity[];
      this.updatePartnership({ ...partnership, virtualIdentities });
      return virtualIdentities;
    },
    async loadSubscriptionForCurrentPartnership() {
      const partnership = this.currentPartnership;
      if (!partnership) return;
      try {
        const res = await apiGw.get(
          createResourceUri('/v2/agent', { uid: partnership.uid, resource: AgentURI.Subscription })
        );
        const subscription = res.data as Subscription;
        this.updatePartnership({ ...partnership, subscription });
        return subscription;
      } catch (error) {
        console.error('Error loading subscription', error);
      }
    },
    async loadDeputiesForCurrentPartnership(mode: RoleAccessMode, updatePartnership = false) {
      const partnership = this.currentPartnership;
      if (!partnership) return;
      const res = await apiGw.get(
        createResourceUri(PartnershipURI.BaseV2, {
          uid: partnership.uid,
          resource: PartnershipURI.Roles,
          queryParams: { roles: ['deputy'], mode: mode }
        })
      );
      const deputies = res.data as Delegate[];
      if (updatePartnership) this.updatePartnership({ ...partnership, deputies });
      return deputies;
    },
    async loadDelegatesForCurrentPartnership(mode: RoleAccessMode, updatePartnership = false) {
      const partnership = this.currentPartnership;
      if (!partnership) return;
      const res = await apiGw.get(
        createResourceUri(PartnershipURI.BaseV2, {
          uid: partnership.uid,
          resource: PartnershipURI.Roles,
          queryParams: { roles: ['delegate'], mode: mode }
        })
      );
      const delegates = res.data as Delegate[];
      if (updatePartnership) this.updatePartnership({ ...partnership, delegates });
      return delegates;
    },
    async loadPartnersForCurrentPartnership(mode: RoleAccessMode, updatePartnership = false) {
      const partnership = this.currentPartnership;
      if (!partnership) return;
      const res = await apiGw.get(
        createResourceUri(PartnershipURI.BaseV2, {
          uid: partnership.uid,
          resource: PartnershipURI.Roles,
          queryParams: { roles: ['partner'], mode: mode }
        })
      );
      const partners = res.data as Delegate[];
      if (updatePartnership) this.updatePartnership({ ...partnership, partners });
      return partners;
    },
    setPendingUpdatePartnership(value: boolean) {
      this.pendingUpdatePartnership = value;
    },
    getDelegateFromUid(uidList: string[]): Delegate[] | undefined {
      const partnership = this.currentPartnership;
      if (!partnership) return undefined;
      const deputies = partnership.deputies || [];
      const delegates = partnership.delegates || [];
      const partner = partnership.partners || [];
      return [...deputies, ...delegates, ...partner].filter((deputy) => uidList.includes(deputy.personUid));
    }
  }
});
